import { Box, Button } from '@material-ui/core';
import { DocumentationRequiredType, RequiredFile } from '@nemuru-eng/js-api-client';

import { NonUploadedFileCard } from '@/components/modules/Documentation/components/DocumentationDetails/components/UploadFileManager/components/NonUploadedFileCard';
import { UploadedFileCard } from '@/components/modules/Documentation/components/DocumentationDetails/components/UploadFileManager/components/UploadedFileCard';
import { useUploadFile } from '@/components/modules/Documentation/components/DocumentationDetails/components/UploadFileManager/hooks/useUploadFile';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { StandardActionDialog } from '@/components/shared/components/StandardActionDialog/StandardActionDialog';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

export interface UploadFileManagerProps {
  loanId: string;
  documentationRequiredId: string;
  requiredFile: RequiredFile;
  opened: boolean;
  setOpened: (opened: boolean) => void;
  setActionDialogParams: (any) => void;
  documentationRequiredType: DocumentationRequiredType;
  onUploadSuccess?: () => void;
}

export const UploadFileManager = ({
  loanId,
  documentationRequiredId,
  requiredFile,
  opened,
  setOpened,
  setActionDialogParams,
  documentationRequiredType,
  onUploadSuccess,
}: UploadFileManagerProps) => {
  const { palette } = useCustomTheme();

  const { documentationToUpload, addFile, removeFile, onUploadDocumentation, fileNameAlreadyUsed, requestLoading } =
    useUploadFile({
      loanId,
      documentationRequiredId,
      requiredFile,
      documentationRequiredType,
    });

  return (
    <StandardActionDialog
      opened={opened}
      setOpened={setOpened}
      setActionDialogParams={setActionDialogParams}
      titleTextElement={<Text tId={requiredFile.name} />}
      actionDescriptionTextElement={<>{requiredFile.description}</>}
      actionButtonsArray={[
        {
          onClick: async () => {
            await onUploadDocumentation();
            if (onUploadSuccess) {
              onUploadSuccess();
            }
            setOpened(false);
          },
          buttonLabelTextId: 'UPLOAD_FILES',
          disabled: documentationToUpload.length === 0 || requestLoading,
        },
      ]}
    >
      <Box pt={2}>
        <CustomPaper fill="grey.light">
          <Box px={4} py={2} display="flex" alignItems="center" justifyContent="center">
            <input
              value={''}
              type="file"
              accept="image/jpeg, image/png, application/pdf"
              id={requiredFile.name}
              name={requiredFile.name}
              key={requiredFile.name}
              style={{
                display: 'none',
              }}
              onChange={(e) => addFile(e, requiredFile)}
            />
            <label htmlFor={requiredFile.name}>
              <Button size={'small'} color="secondary" variant="contained" component="span">
                <i className={'material-icons-outlined pr-2'}>add</i>
                <Text tId="ADD_FILE_ACTION" />
              </Button>
            </label>
          </Box>
        </CustomPaper>
      </Box>
      <Box>
        {documentationToUpload.length > 0 && (
          <>
            <FlexColumn gridGap={16} pt={3}>
              <Font variant={'subtitle2'}>
                <Text strong tId={'DOCUMENTS_TO_UPLOAD'} />
              </Font>
              {documentationToUpload.map((file, i) => (
                <NonUploadedFileCard
                  file={file}
                  removeFileFromContext={() => removeFile(file.name)}
                  key={i}
                  loading={requestLoading}
                />
              ))}
              {fileNameAlreadyUsed && (
                <FlexRow style={{ color: palette.error.main }} alignItems="flex-start">
                  <Icon size={18}>info</Icon>
                  <Font variant="caption">
                    <Text tId={'FILE_WILL_BE_OVERWRITTEN'} />
                  </Font>
                </FlexRow>
              )}
            </FlexColumn>
          </>
        )}
        {requiredFile.uploadedFiles && requiredFile.uploadedFiles.length > 0 && (
          <FlexColumn pt={3} gridGap={16}>
            <Font variant={'subtitle2'}>
              <Text strong tId={'UPLOADED_DOCUMENTATION'} />
            </Font>
            {requiredFile.uploadedFiles?.map((file, i) => (
              <UploadedFileCard file={file} key={i} />
            ))}
          </FlexColumn>
        )}
      </Box>
    </StandardActionDialog>
  );
};
